import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, CheckCircle2, KeyRound, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { EmailRequiredError, useFirebaseAuth } from "@/hooks/useFirebaseAuth";

type Stage = "idle" | "sending" | "sent" | "completing" | "needs_email" | "error";

const RESEND_COOLDOWN = 60;

export function LoginPage() {
  const { user, sendLink, isLink, completeFromLink } = useFirebaseAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [stage, setStage] = useState<Stage>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const href = window.location.href;
    if (!isLink(href)) return;
    setStage("completing");
    completeFromLink(href)
      .then(() => navigate("/", { replace: true }))
      .catch((err: Error) => {
        if (err instanceof EmailRequiredError) {
          setStage("needs_email");
          return;
        }
        setErrorMsg(err.message);
        setStage("error");
      });
  }, [isLink, completeFromLink, navigate]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  async function sendOnce() {
    setStage("sending");
    setErrorMsg(null);
    try {
      await sendLink(email.trim());
      setStage("sent");
      setResendIn(RESEND_COOLDOWN);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Errore invio link");
      setStage("error");
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    await sendOnce();
  }

  async function handleConfirmEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStage("completing");
    setErrorMsg(null);
    try {
      await completeFromLink(window.location.href, email.trim());
      navigate("/", { replace: true });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Errore completamento accesso");
      setStage("error");
    }
  }

  const item = {
    hidden: { opacity: 0, y: 8 },
    show: { opacity: 1, y: 0 },
  };
  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-b from-brand-50/60 via-background to-background">
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="w-full max-w-md flex flex-col items-center"
      >
        <motion.img
          variants={item}
          src="/logo-misterpet-verdone.png"
          alt="Mister Pet"
          className="h-28 w-auto mb-10 select-none"
          draggable={false}
        />

        <motion.p
          variants={item}
          className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2"
        >
          Operations Hub
        </motion.p>

        <motion.h1
          variants={item}
          className="text-2xl font-semibold tracking-tight text-foreground mb-8"
        >
          Accesso al portale
        </motion.h1>

        <motion.div variants={item} className="w-full">
          <AnimatePresence mode="wait">
            {stage === "completing" && (
              <motion.div
                key="completing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-8"
              >
                <Loader2 className="size-4 animate-spin" />
                <span>Completamento accesso in corso…</span>
              </motion.div>
            )}

            {stage === "needs_email" && (
              <motion.form
                key="needs_email"
                onSubmit={handleConfirmEmail}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="space-y-5"
              >
                <div className="text-center space-y-2">
                  <p className="text-base font-medium">Conferma email</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Per completare l'accesso, inserire l'email a cui è stato richiesto il link.
                  </p>
                </div>
                <input
                  id="email-confirm"
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome.cognome@azienda.it"
                  className="w-full h-12 rounded-md border border-input bg-background px-4 text-base outline-none transition-all placeholder:text-muted-foreground/60 focus:ring-2 focus:ring-brand-700/20 focus:border-brand-700"
                />
                <Button
                  type="submit"
                  disabled={!email.trim()}
                  className="w-full h-12 text-sm font-medium shadow-sm hover:shadow transition-shadow"
                >
                  Conferma e accedi
                </Button>
              </motion.form>
            )}

            {stage === "sent" && (
              <motion.div
                key="sent"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="text-center space-y-5"
              >
                <div className="inline-flex items-center justify-center size-12 rounded-full bg-brand-50 text-brand-700">
                  <CheckCircle2 className="size-6" />
                </div>
                <div className="space-y-2">
                  <p className="text-base font-medium">
                    Email inviata a <span className="text-brand-700">{email}</span>
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Aprire il link dal messaggio per completare l'accesso. Il link
                    funziona solo dallo stesso dispositivo e browser usato qui.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={sendOnce}
                  disabled={resendIn > 0 || stage !== "sent"}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {resendIn > 0
                    ? `Nessuna mail? Invia di nuovo in ${resendIn}s`
                    : "Nessuna mail ricevuta? Invia di nuovo"}
                </button>
              </motion.div>
            )}

            {(stage === "idle" || stage === "sending" || stage === "error") && (
              <motion.form
                key="form"
                onSubmit={handleSubmit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="text-xs font-medium text-muted-foreground"
                  >
                    Indirizzo email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome.cognome@azienda.it"
                    disabled={stage === "sending"}
                    className="w-full h-12 rounded-md border border-input bg-background px-4 text-base outline-none transition-all placeholder:text-muted-foreground/60 focus:ring-2 focus:ring-brand-700/20 focus:border-brand-700"
                  />
                </div>

                <AnimatePresence>
                  {errorMsg && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex items-start gap-2 text-xs text-destructive overflow-hidden"
                    >
                      <AlertCircle className="size-3.5 mt-0.5 shrink-0" />
                      <span>{errorMsg}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                <Button
                  type="submit"
                  disabled={stage === "sending" || !email.trim()}
                  className="w-full h-12 text-sm font-medium shadow-sm hover:shadow transition-shadow"
                >
                  {stage === "sending" ? (
                    <>
                      <Loader2 className="size-4 animate-spin mr-2" />
                      Invio in corso…
                    </>
                  ) : (
                    <>
                      <KeyRound className="size-4 mr-2" />
                      Ricevi il link di accesso
                    </>
                  )}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.p
          variants={item}
          className="text-[11px] text-muted-foreground text-center mt-10 max-w-xs leading-relaxed"
        >
          Accesso riservato agli utenti autorizzati. Per assistenza scrivere a{" "}
          <a
            href="mailto:operations@maitime.ai"
            className="text-foreground hover:text-brand-700 transition-colors"
          >
            operations@maitime.ai
          </a>
          .
        </motion.p>
      </motion.div>
    </div>
  );
}
